export const SmartFolderErrorState = {
	OwnerDeactivated: 'owner-deactivated',
	OwnerLostAccess: 'owner-lost-access',
	FolderDeleted: 'folder-deleted',
	SyncTransientError: 'sync-transient-error',
} as const;

export type SmartFolderErrorState =
	(typeof SmartFolderErrorState)[keyof typeof SmartFolderErrorState];

/**
 * Known structured error types returned by the confluence_syncSmartFolder mutation.
 *
 * These values come from the `extensions[].errorType` field of the mutation response.
 *
 * TODO(CCINT-8155): Once the BE schema change is deployed and the Relay-generated
 * types include `extensions { errorType statusCode }`, remove the string literals
 * here and import the generated enum instead.
 */
export const SyncErrorType = {
	OwnerIneligible: 'SYNC_CONFLUENCE_OWNER_INELIGIBLE',
	RemoteOwnerLostAccess: 'SYNC_REMOTE_OWNER_LOST_ACCESS',
	RemoteOwnerDeactivated: 'SYNC_REMOTE_OWNER_DEACTIVATED',
	RemoteFolderDeleted: 'SYNC_REMOTE_FOLDER_DELETED',
	TransientError: 'SYNC_TRANSIENT_ERROR',
} as const;

type SyncErrorType = (typeof SyncErrorType)[keyof typeof SyncErrorType];

/**
 * Maps a structured sync errorType to the corresponding SmartFolderErrorState
 * for display in the error screen. Returns null for unrecognised / transient
 * errors (e.g. SYNC_TRANSIENT_ERROR) so callers can fall back to inline error
 * handling.
 *
 * TODO(CCINT-8155): Replace the `errorType: string` parameter type with the
 * Relay-generated extensions type once the BE schema is deployed.
 *
 * TODO#2: To reduce complexity, we could consider relying solely on the server types.
 */
export const getSyncErrorState = (errorType: string): SmartFolderErrorState | null => {
	switch (errorType) {
		case SyncErrorType.OwnerIneligible:
		case SyncErrorType.RemoteOwnerDeactivated:
			return SmartFolderErrorState.OwnerDeactivated;
		case SyncErrorType.RemoteOwnerLostAccess:
			return SmartFolderErrorState.OwnerLostAccess;
		case SyncErrorType.RemoteFolderDeleted:
			return SmartFolderErrorState.FolderDeleted;
		case SyncErrorType.TransientError:
			return SmartFolderErrorState.SyncTransientError;
		default:
			return null;
	}
};
